import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';

export default class extends Controller {
  static outlets = ['billable-form'];

  ungroup(event) {
    event.preventDefault();
    this.dissolveGroup();
  }

  removeFromGroup(event) {
    event.preventDefault();

    const item = this.element;
    const subitems = item.parentElement;
    const primary = subitems.closest('.billable-item');

    this.#resetGroupItem(item);
    primary.after(item);
    this.#resetPrimaryIfEmpty(subitems);
    this.#syncSectionCounts();
  }

  dissolveGroup() {
    const primary = this.element.parentElement.closest('.billable-item') ?? this.element;
    const subitems = primary.querySelector(':scope > .subitems');
    const secondaryItems = subitems.querySelectorAll(':scope > .billable-item');

    this.#resetGroupItem(primary);
    secondaryItems.forEach((item) => this.#resetGroupItem(item));
    primary.after(...secondaryItems);
    this.#syncSectionCounts();
  }

  toggleSelection(event) {
    if (event.target.hasAttribute('data-readonly')) {
      event.target.checked = true;
      return;
    }

    // The row's switch owns its label now; .df-switch is the component's stable hook.
    event.target
      .closest('.data-table__tr')
      .querySelector('.df-switch')
      ?.closest('label')
      ?.classList.toggle('opacity-50', event.target.checked === false);
  }

  togglePrimary(event) {
    const target = event.target;
    const table = this.element.querySelector('.data-table__table');

    if (!target.checked) {
      table
        .querySelectorAll('.data-table__tr:not(.data-table__tr--indented) .checkbox--group-item')
        .forEach((el) => el.removeAttribute('data-readonly'));

      table
        .querySelectorAll('.data-table__tr:not(.data-table__tr--indented)')
        .forEach((el) => el.classList.add('data-table__tr--indented'));
      return;
    }

    table
      .querySelectorAll('.data-table__tr:not(.data-table__tr--indented) .checkbox--group-item')
      .forEach((el) => el.removeAttribute('data-readonly'));

    table
      .querySelectorAll(
        '.data-table__tr:not(.data-table__tr--indented) .green-checkbox--group-item-primary'
      )
      .forEach((el) => (el.checked = false));

    table
      .querySelectorAll('.data-table__tr:not(.data-table__tr--indented)')
      .forEach((el) => el.classList.add('data-table__tr--indented'));

    const targetRow = target.closest('tr');
    targetRow.classList.remove('data-table__tr--indented');
    targetRow
      .querySelectorAll('.checkbox--group-item')
      .forEach((el) => el.setAttribute('data-readonly', ''));
    table.querySelector('tbody').prepend(targetRow);
  }

  updateGroupOnForm(event) {
    event.preventDefault();

    if (this.#hasSelectedPrimaryItem() && this.#hasSelectedSecondaryItems()) {
      this.element.querySelector('.error-message--group-items').style.display = 'none';
    } else {
      this.element.querySelector('.error-message--group-items').style.display = 'block';
      return false;
    }

    const sectionsBefore = this.#sectionsByRowId();
    const primaryRow = this.#rebuildGroupOnForm();
    this.#persistGroupSectionMove(primaryRow, sectionsBefore);

    this.element.closest('dialog').close();
    const billableForm = this.billableFormOutlet.element;
    billableForm
      .querySelectorAll('.checkbox--group-billable-item')
      .forEach((el) => (el.checked = false));
    billableForm.querySelector('.group-selected-items')?.classList.add('hide');
    billableForm
      .querySelector('[data-batch-action-bar-identifier-value="billable-items"]')
      ?.dispatchEvent(new CustomEvent('cancel'));
  }

  get #itemsGrid() {
    return this.billableFormOutlet.itemsGridTarget;
  }

  #rebuildGroupOnForm() {
    const groupState = this.#extractGroupStateFromModal();
    const container = this.#itemsGrid;
    const groupDisplay = this.element.querySelector('.radio--group-display:checked').value;
    let parentSubitems;
    let primaryItemId;
    let primaryRow;

    groupState.forEach(({ id, removed }, index) => {
      const isPrimary = index === 0;
      const item = container.querySelector(`.billable-item[data-id="${id}"]`);
      const previousSubitems = item.parentElement.classList.contains('subitems')
        ? item.parentElement
        : null;
      this.#resetGroupItem(item);

      if (removed) {
        this.#placeInSection(item, item.closest('.billable-section') ?? container);
        if (previousSubitems) {
          this.#resetPrimaryIfEmpty(previousSubitems);
        }

        return;
      }

      if (isPrimary) {
        primaryItemId = id;
        primaryRow = item;
        if (previousSubitems) {
          previousSubitems.closest('.billable-item').before(item);
          this.#resetPrimaryIfEmpty(previousSubitems);
        }
        item.querySelector('.billable-item__group-display').value = groupDisplay;
        parentSubitems = item.querySelector(':scope > .subitems');
      } else {
        item.querySelector('.billable-item__primary-id').value = primaryItemId;
        parentSubitems.appendChild(item);
        if (previousSubitems && previousSubitems !== parentSubitems) {
          this.#resetPrimaryIfEmpty(previousSubitems);
        }
      }
    });

    return primaryRow;
  }

  #sectionsByRowId() {
    const container = this.#itemsGrid;

    return new Map(
      this.#extractGroupStateFromModal().map(({ id }) => [
        id,
        container.querySelector(`.billable-item[data-id="${id}"]`)?.closest('.billable-section'),
      ])
    );
  }

  async #persistGroupSectionMove(primaryRow, sectionsBefore) {
    const destination = primaryRow?.closest('.billable-section');

    if (!destination) {
      return;
    }

    const rows = [primaryRow, ...primaryRow.querySelectorAll('.billable-item')];

    if (rows.every((row) => sectionsBefore.get(row.dataset.id) === destination)) {
      this.#syncSectionCounts();
      return;
    }

    const body = new FormData();
    rows.forEach((row) => body.append('item_ids[]', row.dataset.id));
    if (!destination.classList.contains('billable-section--unassigned')) {
      body.append('section_id', destination.dataset.sectionId);
    }
    body.append(
      'position',
      [...destination.querySelectorAll('.billable-item')].indexOf(primaryRow)
    );

    try {
      const response = await new FetchRequest(
        'post',
        destination.dataset.billableSortableMoveUrlValue,
        {
          body,
        }
      ).perform();

      if (!response.ok) {
        window.alert(
          'The bundle could not be moved into its section. Please reload and try again.'
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#syncSectionCounts();
    }
  }

  #placeInSection(row, section) {
    const addItemBar = section.querySelector(':scope > .billable-section__add-item');

    if (addItemBar) {
      addItemBar.before(row);
    } else {
      section.append(row);
    }
  }

  #syncSectionCounts() {
    this.dispatch('items-moved', { prefix: 'billable-section' });
  }

  #extractGroupStateFromModal() {
    return Array.from(this.element.querySelectorAll('.data-table__tr')).map((row) => {
      return {
        id: row.dataset.id,
        removed: !row.querySelector('.checkbox--group-item').checked,
      };
    });
  }

  #resetGroupItem(item) {
    item.querySelector('.billable-item__primary-id').value = 0;
    item.querySelector('.billable-item__group-display').value = 'primary_and_secondary';
  }

  #resetPrimaryIfEmpty(subitems) {
    if (subitems.querySelector('.billable-item')) {
      return;
    }

    const primary = subitems.closest('.billable-item');
    if (primary) {
      this.#resetGroupItem(primary);
    }
  }

  #hasSelectedPrimaryItem() {
    return this.element.querySelectorAll('.green-checkbox--group-item-primary:checked').length > 0;
  }

  #hasSelectedSecondaryItems() {
    return (
      this.element.querySelectorAll('.data-table__tr--indented .checkbox--group-item:checked')
        .length > 0
    );
  }
}
